import type {LocalizationService} from 'shared/providers';
import type {DirectoryNode, PayloadActionResponse, RawBodyMailSendRequest, ViewableFile} from 'src/typings';
import type {DirectoryDataService} from 'shared/data-services';
import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {MailerService} from 'shared/mailer';
import type {ModalDialogService} from 'shared/modal-dialogs';
import type {FinishedResponseInteractionService, WalkerService} from 'shared/services';
import type {Searcher, SearcherFactory} from './mv-searcher.factory';
import type {MoveFileResult} from 'src/features/media-viewer/lib/mv-constants';
import type {MediaViewerFile, MvMoveFileModalModel} from 'src/features/media-viewer/lib/mv-types';
import type {FileService} from '../../file/file.service';
import type {SearchService} from '../../search/search.service';
import type {FileDownloadService} from 'core/data-services/file-download.service';
import type {FileContentDataService} from '../../file/file-content.data-service';
import type {ChunkFileUploadService} from 'core/data-services/chunk-file-upload.service';
import {FileViewForm, Kind, SearchType} from 'shared/constants';
import MvMoveFileModal from '../components/mv-misc/mv-move-file-modal/MvMoveFileModal.svelte';
import {exists, isFunction} from 'shared/utils/custom-utils';

export class MvFileExplorerService {
    public static serviceName = 'mediaViewerFileExplorerService';

    /*@ngInject*/
    constructor(private fileService: FileService,
                private fileDownloadService: FileDownloadService,
                private searchService: SearchService,
                private searcherFactory: SearcherFactory<MediaViewerFile>,
                private directoryDataService: DirectoryDataService,
                private walker: WalkerService,
                private toastMessageService: ToastMessageService,
                private mailerService: MailerService,
                private localizationService: LocalizationService,
                private fileContentDataService: FileContentDataService,
                private chunkFileUploadService: ChunkFileUploadService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private modalDialogService: ModalDialogService) {
    }

    public createFileSearcher(rootDirectory: DirectoryNode): Searcher<MediaViewerFile> {
        return this.searcherFactory.create(
            async (query) => {
                const params = {
                    kind: [Kind.KIND_FILE],
                    type: SearchType.TYPE_FILE_SEARCH,
                    q: query,
                    pageSize: 50,
                    rootDirectory: rootDirectory.id
                };
                const searchResult = await this.searchService.search<MediaViewerFile>(params);
                return searchResult.result.content;
            });
    }

    public getFilesByDirectory(directoryId: number): Promise<MediaViewerFile[]> {
        return this.fileService.getFilesByDirectory(directoryId);
    }

    public getFileById(id: number): Promise<MediaViewerFile> {
        return this.fileService.getFileById(id);
    }

    public async createSubdirectory(currentDirectory: DirectoryNode): Promise<DirectoryNode | null> {
        try {
            const actionResponse = await this.directoryDataService.createSubdirectory(currentDirectory.id);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            return {
                ...actionResponse.savedObject,
                children: []
            };
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return null;
        }
    }

    public async editSelectedDirectory(currentDirectory: DirectoryNode): Promise<DirectoryNode | null> {
        try {
            const actionResponse = await this.directoryDataService.editDirectory(currentDirectory);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            return {
                ...actionResponse.savedObject,
                children: currentDirectory.children
            };
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return null;
        }
    }

    public async deleteSelectedDirectory(currentDirectory: DirectoryNode): Promise<boolean> {
        try {
            const actionResponse = await this.directoryDataService.deleteDirectory(currentDirectory);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            return true;
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return false;
        }
    }

    public async editFileMetadata(file: MediaViewerFile): Promise<MediaViewerFile | null> {
        try {
            const actionResponse = await this.fileService.editFileMetadata(file);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            return actionResponse.savedObject;
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return null;
        }
    }

    public async printFiles(files: MediaViewerFile[]): Promise<void> {
        const fileIdsUrlParam = files.map((file) => file.id).join(',');
        await this.walker.newPage('/export', {target: 'files-print:/html/files-print.vtl', object: fileIdsUrlParam});
    }

    public async printFile(file: MediaViewerFile): Promise<void> {
        if (file.viewForms.includes(FileViewForm.PDF)) {
            this.toastMessageService.showWarning('PDF soubory nezle tisknout přímo ze stránky. Stáhněte prosím tento soubor a vytiskněte pomocí programu.');
            return;
        }

        await this.walker.newPage('/export', {
            target: 'files-print:/html/files-print.vtl',
            object: file.id
        });
    }

    public async mailFiles(files: MediaViewerFile[]): Promise<void> {
        const request: RawBodyMailSendRequest = {
            recipientEmails: null,
            subject: /* @kp-localization @kp-localization mediaviewer.mail.Subject */ this.localizationService.get('mediaviewer.mail.Subject'),
            attachments: files,
            body: /* @kp-localization mediaviewer.mail.Body */ this.localizationService.get('mediaviewer.mail.Body')
        };
        return this.mailerService.sendMail(request);
    }

    public async deleteFiles(files: MediaViewerFile[]): Promise<boolean> {
        try {
            const promises = files.map((file) => this.fileService.deleteFile(file));
            await Promise.all(promises);
            this.toastMessageService.showSuccess('Soubory smazány');
            return true;
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return false;
        }
    }

    public async createFile(parentDirectory: DirectoryNode): Promise<MediaViewerFile | null> {
        try {
            return await this.fileService.createNewFile(parentDirectory.id);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return null;
        }
    }

    public async uploadFiles(parentDirectory: DirectoryNode, files: File[], progressCallback?: (percentageValue: number) => void): Promise<MediaViewerFile[]> {
        const totalSize = files.reduce((acc, file) => acc + file.size, 0);
        const fileUploadProgresses = Array(files.length).fill(0);

        const calculateTotalProgressPercentage = () => {
            let uploadedSize = 0;

            fileUploadProgresses.forEach((progress, index) => {
                uploadedSize += files[index].size * (progress / 100);
            });

            const totalProgressPercentage = (uploadedSize / totalSize) * 100;

            if (isFunction(progressCallback)) {
                progressCallback(totalProgressPercentage);
            }
        };

        const uploadedFiles: MediaViewerFile[] = [];

        const uploadPromises = files.map((file) => {
            const chunkFileUploader = this.chunkFileUploadService.createUploader<ViewableFile>({
                uploadEndpoint: 'files/chunk-file-upload/directory',
                file,
                additionalData: {
                    targetDirectory: parentDirectory.id
                },
                onProgressUpdate: (progress) => {
                    const fileIndex = files.indexOf(file);
                    fileUploadProgresses[fileIndex] = progress;
                    calculateTotalProgressPercentage();
                },
                onUploadFinished: (mediaViewerFile) => {
                    if (exists(mediaViewerFile)) {
                        uploadedFiles.push(mediaViewerFile);
                    }
                }
            });

            return chunkFileUploader.upload();
        });

        await Promise.all(uploadPromises);

        return uploadedFiles;
    }

    public async copyUrlToClipboard(): Promise<void> {
        try {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.delete('origin');

            await navigator.clipboard.writeText(currentUrl.toString());
            this.toastMessageService.showInfo('URL odkaz na soubor byl zkopírován do schránky!');
        } catch {
            this.toastMessageService.showError('Nepodařilo se zkopírovat URL do schránky.');
        }
    }

    public async getFileContent(file: MediaViewerFile, type: 'original' | 'thumbnail' | 'text' = 'original'): Promise<string> {
        try {
            return this.fileContentDataService.getFileContent(file, type);
        } catch {
            return '';
        }
    }

    public async downloadFile(file: MediaViewerFile): Promise<void> {
        try {
            await this.fileDownloadService
                .withoutBaseUrl()
                .createRequest(`files/${file.id}?inline=false`)
                .download(file.filename);
        } catch (e) {
            this.toastMessageService.showError(`Nepodařilo se stáhnout soubor: ${e.message}`);
        }
    }

    public async saveFileContents(file: MediaViewerFile, newContent: string): Promise<number | null> {
        try {
            const responseFile = await this.fileService.saveFileContent(file, newContent);
            this.toastMessageService.showSuccess('Změny souboru uloženy!');
            return responseFile.size;
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return null;
        }
    }

    public async downloadAllDirectoryFiles(directory: DirectoryNode): Promise<void> {
        try {
            const folderFiles = await this.getFilesByDirectory(directory.id);

            if (folderFiles.length === 0) {
                this.toastMessageService.showError('Adresář neobsahuje žádné soubory!');
                return;
            }

            await this.fileDownloadService
                .withoutBaseUrl()
                .createRequest(`files/download?directory=${directory.id}&alwaysZip=true`)
                .download(`${directory.name}.zip`);
        } catch (e) {
            this.toastMessageService.showError(`Nepodařilo se stáhnout soubory z adresáře: ${e.message}`);
        }
    }

    public async moveFile(file: MediaViewerFile, rootDirectoryId: number, targetDirectoryId?: number): Promise<MoveFileResult | null> {
        try {
            let selectedDirectoryId = targetDirectoryId;

            if (!exists(selectedDirectoryId)) {
                const rootDirectory = await this.directoryDataService.getDirectoryTree(rootDirectoryId);
                const payloadResponse = await this.modalDialogService.openModalWindowFromComponent<MvMoveFileModalModel, PayloadActionResponse<DirectoryNode | null>>(MvMoveFileModal, {rootDirectory});
                selectedDirectoryId = payloadResponse.payload?.id;
            }

            if (!exists(selectedDirectoryId)) {
                return null;
            }

            const result = await this.fileService.moveFile(file, selectedDirectoryId);
            this.toastMessageService.showSuccess('Soubor přesunut!');

            return {
                targetDirectoryId: selectedDirectoryId,
                originalDirectoryId: file.directory.id,
                updatedFile: result.savedObject
            };
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return null;
        }
    }
}